/**
 * Genera un gráfico SVG de líneas para los votos y lo inserta en el contenedor.
 * Además agrega contadores de votos por tipo.
 * @param {Array} votes - Array de objetos voto { value, createdAt }
 * @param {HTMLElement} container - Elemento contenedor donde renderizar
 */
export const renderVotesChart = (votes, container) => {
    console.log('[VotesChart] Renderizando gráfico con', votes?.length || 0, 'votos');
    
    container.innerHTML = '';
    
    if (!votes || votes.length === 0) {
        console.log('[VotesChart] Sin datos de votos, mostrando mensaje');
        const emptyDiv = document.createElement('div');
        emptyDiv.style.cssText = `
            text-align: center;
            padding: 60px 20px;
            color: #999;
            background: #f9f9f9;
            border-radius: 8px;
            border: 2px dashed #ddd;
        `;
        emptyDiv.innerHTML = `
            <p style="margin: 0; font-size: 16px;">📊 Aún no hay votos registrados.</p>
            <p style="margin: 8px 0 0 0; font-size: 14px; color: #bbb;">Sé el primero en votar 👇</p>
        `;
        container.appendChild(emptyDiv);
        return;
    }

    // 1. Contar votos por tipo
    const voteCounts = { positivo: 0, neutral: 0, negativo: 0 };
    votes.forEach(v => {
        if (v.value && voteCounts.hasOwnProperty(v.value)) {
            voteCounts[v.value]++;
        }
    });
    
    const totalVotes = votes.length;
    console.log('[VotesChart] Contadores:', voteCounts, 'Total:', totalVotes);

    // 2. Crear sección de contadores
    const countersDiv = document.createElement('div');
    countersDiv.className = 'vote-counters';
    countersDiv.style.cssText = `
        display: flex;
        justify-content: space-around;
        gap: 20px;
        margin-bottom: 30px;
        padding: 20px;
        background-color: #f5f5f5;
        border-radius: 8px;
    `;

    const createCounter = (type, emoji, label, count) => {
        const div = document.createElement('div');
        div.style.cssText = `
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
        `;
        
        const emoji_el = document.createElement('span');
        emoji_el.textContent = emoji;
        emoji_el.style.fontSize = '28px';
        
        const label_el = document.createElement('p');
        label_el.textContent = label;
        label_el.style.cssText = `
            margin: 0;
            font-size: 14px;
            color: #666;
            font-weight: 500;
        `;
        
        const count_el = document.createElement('strong');
        count_el.textContent = count;
        count_el.style.cssText = `
            font-size: 24px;
            color: ${getColorForType(type)};
        `;
        
        const percent_el = document.createElement('small');
        const percentage = totalVotes > 0 ? ((count / totalVotes) * 100).toFixed(1) : 0;
        percent_el.textContent = `${percentage}%`;
        percent_el.style.cssText = `
            font-size: 12px;
            color: #999;
        `;
        
        console.log(`[VotesChart] Creando contador: ${label} (${count}) ${percentage}%`);
        
        div.appendChild(emoji_el);
        div.appendChild(label_el);
        div.appendChild(count_el);
        div.appendChild(percent_el);
        
        return div;
    };

    countersDiv.appendChild(createCounter('positivo', '👍', 'Positivo', voteCounts.positivo));
    countersDiv.appendChild(createCounter('neutral', '😐', 'Neutral', voteCounts.neutral));
    countersDiv.appendChild(createCounter('negativo', '👎', 'Negativo', voteCounts.negativo));

    container.appendChild(countersDiv);

    // 3. Procesar datos para el gráfico: Agrupar por fecha (día)
    const dataPoints = {}; // 'YYYY-MM-DD': { positivo: 0, neutral: 0, negativo: 0 }
    
    let minTime = Infinity;
    let maxTime = -Infinity;

    votes.forEach(v => {
        if (!v.createdAt) return;
        
        // Convertir Firestore Timestamp a Date JS
        const date = v.createdAt.toDate ? v.createdAt.toDate() : new Date(v.createdAt);
        const time = date.getTime();
        const dateKey = date.toISOString().split('T')[0];
        
        if (time < minTime) minTime = time;
        if (time > maxTime) maxTime = time;

        if (!dataPoints[dateKey]) {
            dataPoints[dateKey] = { positivo: 0, neutral: 0, negativo: 0, time: time };
        }
        dataPoints[dateKey][v.value]++;
    });

    // Convertir a array y ordenar
    const sortedData = Object.values(dataPoints).sort((a, b) => a.time - b.time);
    
    // Si solo hay un punto, extendemos el rango artificialmente
    if (minTime === maxTime) {
        minTime -= 86400000; // -1 día
        maxTime += 86400000; // +1 día
    }

    // 4. Configurar dimensiones SVG
    const width = 600;
    const height = 300;
    const padding = 40;
    
    // Calcular máximo valor Y para la escala
    let maxY = 0;
    sortedData.forEach(d => {
        maxY = Math.max(maxY, d.positivo, d.neutral, d.negativo);
    });
    maxY = maxY === 0 ? 10 : maxY + 2;

    // Funciones de escala
    const scaleX = (time) => {
        return padding + ((time - minTime) / (maxTime - minTime)) * (width - (padding * 2));
    };

    const scaleY = (val) => {
        return height - padding - ((val / maxY) * (height - (padding * 2)));
    };

    // 5. Generar Paths (líneas)
    const createPathD = (type) => {
        let d = '';
        sortedData.forEach((pt, i) => {
            const x = scaleX(pt.time);
            const y = scaleY(pt[type]);
            d += `${i === 0 ? 'M' : 'L'} ${x} ${y} `;
        });
        return d;
    };

    const pathPos = createPathD('positivo');
    const pathNeu = createPathD('neutral');
    const pathNeg = createPathD('negativo');

    // 6. Construir SVG
    const svgNS = "http://www.w3.org/2000/svg";
    const svg = document.createElementNS(svgNS, "svg");
    svg.setAttribute("viewBox", `0 0 ${width} ${height}`);
    svg.setAttribute("role", "img");
    svg.setAttribute("aria-label", "Gráfico de tendencias de votos");
    svg.style.width = "100%";
    svg.style.height = "auto";
    svg.style.minHeight = "400px";
    svg.style.border = "1px solid #eee";
    svg.style.borderRadius = "8px";

    // Fondo
    const bg = document.createElementNS(svgNS, "rect");
    bg.setAttribute("width", width);
    bg.setAttribute("height", height);
    bg.setAttribute("fill", "#fafafa");
    svg.appendChild(bg);

    // Grid horizontal con etiquetas en Y
    for (let i = 0; i <= maxY; i++) {
        const y = scaleY(i);
        
        // Línea de grid
        const line = document.createElementNS(svgNS, "line");
        line.setAttribute("x1", padding);
        line.setAttribute("y1", y);
        line.setAttribute("x2", width - padding);
        line.setAttribute("y2", y);
        line.setAttribute("stroke", i === 0 ? "#ccc" : "#eee");
        line.setAttribute("stroke-width", "1");
        svg.appendChild(line);
        
        // Etiqueta de número en Y (cantidad)
        const text = document.createElementNS(svgNS, "text");
        text.setAttribute("x", padding - 10);
        text.setAttribute("y", y + 4);
        text.setAttribute("text-anchor", "end");
        text.setAttribute("font-size", "12");
        text.setAttribute("fill", "#666");
        text.textContent = i;
        svg.appendChild(text);
    }

    // Ejes principales
    const xAxis = document.createElementNS(svgNS, "line");
    xAxis.setAttribute("x1", padding);
    xAxis.setAttribute("y1", height - padding);
    xAxis.setAttribute("x2", width - padding);
    xAxis.setAttribute("y2", height - padding);
    xAxis.setAttribute("stroke", "#333");
    xAxis.setAttribute("stroke-width", "2");
    svg.appendChild(xAxis);

    const yAxis = document.createElementNS(svgNS, "line");
    yAxis.setAttribute("x1", padding);
    yAxis.setAttribute("y1", padding);
    yAxis.setAttribute("x2", padding);
    yAxis.setAttribute("y2", height - padding);
    yAxis.setAttribute("stroke", "#333");
    yAxis.setAttribute("stroke-width", "2");
    svg.appendChild(yAxis);

    // Etiquetas en eje X (fechas y horas)
    if (sortedData.length > 0) {
        // Primera fecha y hora
        const firstDate = new Date(sortedData[0].time);
        const firstX = scaleX(sortedData[0].time);
        
        const firstDateText = document.createElementNS(svgNS, "text");
        firstDateText.setAttribute("x", firstX);
        firstDateText.setAttribute("y", height - padding + 15);
        firstDateText.setAttribute("text-anchor", "middle");
        firstDateText.setAttribute("font-size", "10");
        firstDateText.setAttribute("fill", "#666");
        firstDateText.textContent = firstDate.toLocaleDateString('es-ES', { month: '2-digit', day: '2-digit' });
        svg.appendChild(firstDateText);
        
        const firstTimeText = document.createElementNS(svgNS, "text");
        firstTimeText.setAttribute("x", firstX);
        firstTimeText.setAttribute("y", height - padding + 28);
        firstTimeText.setAttribute("text-anchor", "middle");
        firstTimeText.setAttribute("font-size", "10");
        firstTimeText.setAttribute("fill", "#999");
        firstTimeText.textContent = firstDate.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
        svg.appendChild(firstTimeText);
        
        // Última fecha y hora
        const lastDate = new Date(sortedData[sortedData.length - 1].time);
        const lastX = scaleX(sortedData[sortedData.length - 1].time);
        
        const lastDateText = document.createElementNS(svgNS, "text");
        lastDateText.setAttribute("x", lastX);
        lastDateText.setAttribute("y", height - padding + 15);
        lastDateText.setAttribute("text-anchor", "middle");
        lastDateText.setAttribute("font-size", "10");
        lastDateText.setAttribute("fill", "#666");
        lastDateText.textContent = lastDate.toLocaleDateString('es-ES', { month: '2-digit', day: '2-digit' });
        svg.appendChild(lastDateText);
        
        const lastTimeText = document.createElementNS(svgNS, "text");
        lastTimeText.setAttribute("x", lastX);
        lastTimeText.setAttribute("y", height - padding + 28);
        lastTimeText.setAttribute("text-anchor", "middle");
        lastTimeText.setAttribute("font-size", "10");
        lastTimeText.setAttribute("fill", "#999");
        lastTimeText.textContent = lastDate.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
        svg.appendChild(lastTimeText);
    }

    // Etiquetas de ejes
    const yAxisLabel = document.createElementNS(svgNS, "text");
    yAxisLabel.setAttribute("x", 15);
    yAxisLabel.setAttribute("y", padding - 10);
    yAxisLabel.setAttribute("font-size", "12");
    yAxisLabel.setAttribute("fill", "#666");
    yAxisLabel.setAttribute("font-weight", "bold");
    yAxisLabel.textContent = "Votos";
    svg.appendChild(yAxisLabel);

    const xAxisLabel = document.createElementNS(svgNS, "text");
    xAxisLabel.setAttribute("x", width / 2);
    xAxisLabel.setAttribute("y", height - 5);
    xAxisLabel.setAttribute("text-anchor", "middle");
    xAxisLabel.setAttribute("font-size", "12");
    xAxisLabel.setAttribute("fill", "#666");
    xAxisLabel.setAttribute("font-weight", "bold");
    xAxisLabel.textContent = "Fecha y Hora";
    svg.appendChild(xAxisLabel);

    // Línea para Positivo
    const linePos = document.createElementNS(svgNS, "path");
    linePos.setAttribute("d", pathPos);
    linePos.setAttribute("stroke", "#4caf50");
    linePos.setAttribute("stroke-width", "2");
    linePos.setAttribute("fill", "none");
    svg.appendChild(linePos);

    // Línea para Neutral
    const lineNeu = document.createElementNS(svgNS, "path");
    lineNeu.setAttribute("d", pathNeu);
    lineNeu.setAttribute("stroke", "#ffc107");
    lineNeu.setAttribute("stroke-width", "2");
    lineNeu.setAttribute("fill", "none");
    svg.appendChild(lineNeu);

    // Línea para Negativo
    const lineNeg = document.createElementNS(svgNS, "path");
    lineNeg.setAttribute("d", pathNeg);
    lineNeg.setAttribute("stroke", "#f44336");
    lineNeg.setAttribute("stroke-width", "2");
    lineNeg.setAttribute("fill", "none");
    svg.appendChild(lineNeg);

    // Leyenda
    const addLegend = (color, text, xPos) => {
        const circle = document.createElementNS(svgNS, "circle");
        circle.setAttribute("cx", xPos);
        circle.setAttribute("cy", 20);
        circle.setAttribute("r", "4");
        circle.setAttribute("fill", color);
        svg.appendChild(circle);

        const txt = document.createElementNS(svgNS, "text");
        txt.setAttribute("x", xPos + 10);
        txt.setAttribute("y", 25);
        txt.textContent = text;
        txt.setAttribute("font-size", "12");
        txt.setAttribute("fill", "#666");
        svg.appendChild(txt);
    };

    addLegend("#4caf50", "Positivo", padding);
    addLegend("#ffc107", "Neutral", padding + 80);
    addLegend("#f44336", "Negativo", padding + 160);

    container.appendChild(svg);
};

/**
 * Obtiene el color para cada tipo de voto
 * @param {string} type - Tipo de voto: 'positivo', 'neutral', 'negativo'
 * @returns {string} Color hex
 */
const getColorForType = (type) => {
    const colors = {
        positivo: '#4caf50',
        neutral: '#ffc107',
        negativo: '#f44336'
    };
    return colors[type] || '#999';
};
